import { useState, useEffect, useRef, useCallback } from 'react';
import { NotificationContext } from '../contexts/NotificationContext.jsx';
import { campusToGuid, userToGuid } from '../libs.jsx';
import { Amplify } from '@aws-amplify/core';
import { generateClient } from '@aws-amplify/api';
import ApiRequester from './ApiRequester.jsx';
import { getSession } from "../services/session";

// Storage keys for active alarms in sessionStorage
const ACTIVE_ALARMS_STORAGE_KEY = 'activeAlarms';
const ACTIVE_CLOUD_ALARMS_STORAGE_KEY = 'activeCloudAlarms';
const SESSION_CHECK_INTERVAL_MS = 3000;
const RECONCILE_INTERVAL_MS = 60000;    // Full alarm refresh interval (only when alert page is active)
const STALE_THRESHOLD_MS = 90000;       // Mark stale if no subscription message in 90s (only when alert page is active)
const RECONNECT_DELAY_MS = 2000;        // Delay before reconnecting after online/visibility recovery
const SLEEP_THRESHOLD_MS = 30000;       // If tab was hidden for >30s, assume sleep/wake — force reconnect

const DEVMODE = import.meta.env.VITE_DEVMODE === 'true';

// 4.0 schema subscription (CampusId: String!, PascalCase fields)
const SUBSCRIPTION_QUERY_40 = `
    subscription OnEmergencyAlert($CampusId: String!) {
        onEmergencyAlert(CampusId: $CampusId) {
            CampusId
            AlarmId
            EventDateTime
            AlertType
            AlertSubType
            Status
            DeviceId
            DeviceUse
            GroupIds
            SerialNumber
            LocationId
            LocationName
            LocationArea
            Near
            AcceptUsername
            AcceptDateTime
            Residents
            MacAddr
            AckDateTime
            Version
        }
    }
`;

// 5.0 schema subscription (campus_id: Int!) – full Alarm type per schema2
const SUBSCRIPTION_QUERY_50 = `
    subscription OnEmergencyAlert($campus_id: Int!) {
        onEmergencyAlert(campus_id: $campus_id) {
            campus_id
            event_id
            accepted_datetime_utc
            accepted_display
            accepted_username
            accepted_user_id
            accept_required
            ack_datetime_utc
            area_group_id
            area_group_name
            area_id
            area_name
            arrived_datetime_utc
            associated_event_id
            autocleartime
            autofinishtime
            device_description
            device_id
            device_name
            device_type_id
            device_use_id
            event_category
            event_category_id
            event_datetime_utc
            event_type
            event_type_id
            finish_datetime_utc
            finish_required
            group_ids
            latitude
            longitude
            radian
            manufacture_id
            manufacture_uuid
            near_area_id
            near_area_name
            residents {
                resident_first_name
                resident_last_name
                resident_name
                resident_id
            }
            version
        }
    }
`;

/**
 * Get AppSync API URL and key for 4.0 (is_cloud false) or 5.0 (is_cloud true).
 * @param {boolean} isCloud - true for 5.0 schema, false for 4.0
 * @returns {{ apiUrl: string, apiKey: string|undefined }}
 */
const getAppSyncConfig = (isCloud) => {
    if (isCloud) {
        return {
            apiUrl: DEVMODE ? import.meta.env.VITE_DEV_APPSYNC_50_API_URL : import.meta.env.VITE_APPSYNC_50_API_URL,
            apiKey: DEVMODE ? import.meta.env.VITE_DEV_APPSYNC_50_API_KEY : import.meta.env.VITE_APPSYNC_50_API_KEY
        };
    }
    return {
        apiUrl: DEVMODE ? import.meta.env.VITE_DEV_APPSYNC_API_URL : import.meta.env.VITE_APPSYNC_API_URL,
        apiKey: DEVMODE ? import.meta.env.VITE_DEV_APPSYNC_API_KEY : import.meta.env.VITE_APPSYNC_API_KEY
    };
};

/**
 * Derive status for 5.0 cloud alarms from accept/ack datetime fields.
 * @param {Object} alarm - 5.0 Alarm with accepted_datetime_utc, ack_datetime_utc (or accepted_display)
 * @returns {'Waiting'|'Accepted'|'Cleared'|'Arrived'}
 */
const OVERDUE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

const isOverdue = (eventDatetimeUtc) => {
    if (!eventDatetimeUtc) return false;
    const eventTime = new Date(eventDatetimeUtc.endsWith('Z') ? eventDatetimeUtc : eventDatetimeUtc + 'Z').getTime();
    return Date.now() - eventTime > OVERDUE_THRESHOLD_MS;
};

const deriveCloudAlarmStatus = (alarm) => {
    const hasAccept = (alarm?.accepted_datetime_utc != null && alarm.accepted_datetime_utc !== '') ||
        (alarm?.accepted_display != null && alarm.accepted_display !== '');
    const hasAck = alarm?.ack_datetime_utc != null && alarm.ack_datetime_utc !== '';
    if (!hasAccept && !hasAck && isOverdue(alarm?.event_datetime_utc)) return 'Overdue';
    if (!hasAccept && !hasAck) return 'Waiting';
    if (hasAccept && !hasAck) return 'Accepted';
    if (!hasAccept && hasAck) return 'Cleared';
    return 'Arrived';
};

/**
 * Derive Status from AcceptDateTime and AckDateTime (4.0 schema).
 * @param {Object} alarm - Alarm object with optional AcceptDateTime, AckDateTime
 * @returns {'Waiting'|'Accepted'|'Cleared'|'Arrived'}
 */
const deriveAlarmStatus = (alarm) => {
    const hasAccept = alarm?.AcceptDateTime != null && alarm.AcceptDateTime !== '';
    const hasAck = alarm?.AckDateTime != null && alarm.AckDateTime !== '';
    if (!hasAccept && !hasAck && isOverdue(alarm?.EventDateTime)) return 'Overdue';
    if (!hasAccept && !hasAck) return 'Waiting';
    if (hasAccept && !hasAck) return 'Accepted';
    if (!hasAccept && hasAck) return 'Cleared';
    return 'Arrived';
};

/**
 * Filter 4.0 alarms by Version when the campus is in hybrid mode.
 * - If is_hybrid is falsy, or both show_new and show_legacy are false → no filter (show all).
 * - If show_new is truthy → include alarms where Version === "4.0".
 * - If show_legacy is truthy → include alarms where Version === "2.0".
 * Alarms without a Version field default to "2.0".
 */
const passesHybridFilter = (alarm, dData) => {
    if (!dData?.hybrid) return true;
    const showNew = !!dData.new;
    const showLegacy = !!dData.legacy;
    if (!showNew && !showLegacy) return true;
    if (alarm?.Version == null || alarm.Version === '') return true;
    const version = alarm.Version.toString();
    if (showNew && version === '4.0') return true;
    if (showLegacy && version === '2.0') return true;
    return false;
};

// Storage utility functions
const AlarmStorage = {
    /**
     * Get all active alarms from sessionStorage
     * @returns {Array} Array of active alarm objects
     */
    getActiveAlarms: () => {
        try {
            const stored = sessionStorage.getItem(ACTIVE_ALARMS_STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error reading active alarms from storage:', error);
            return [];
        }
    },

    /**
     * Save alarms array to sessionStorage
     * @param {Array} alarms - Array of alarm objects to store
     */
    saveActiveAlarms: (alarms) => {
        try {
            sessionStorage.setItem(ACTIVE_ALARMS_STORAGE_KEY, JSON.stringify(alarms));
        } catch (error) {
            console.error('Error saving active alarms to storage:', error);
        }
    },

    /**
     * Add or update an alarm in storage
     * @param {Object} alarm - Alarm object to add or update
     */
    addOrUpdateAlarm: (alarm) => {
        if (!alarm || !alarm.AlarmId) {
            console.warn('Invalid alarm data, cannot store:', alarm);
            return;
        }

        const alarms = AlarmStorage.getActiveAlarms();
        const existingIndex = alarms.findIndex(a => a.AlarmId === alarm.AlarmId);
        const mergedAlarm = existingIndex >= 0
            ? { ...alarms[existingIndex], ...alarm }
            : { ...alarm };
        mergedAlarm.Status = deriveAlarmStatus(mergedAlarm);
        // Parse Residents string into ResidentsObject for front-end use
        try {
            const residentsRaw = mergedAlarm.Residents;
            mergedAlarm.ResidentsObject = residentsRaw != null && residentsRaw !== ''
                ? (typeof residentsRaw === 'string' ? JSON.parse(residentsRaw) : residentsRaw)
                : null;
        } catch (_) {
            mergedAlarm.ResidentsObject = null;
        }
        // Parse Near string (JSON array) into NearObject and derive NearName from each record's name
        try {
            const nearRaw = mergedAlarm.Near;
            const nearArr = nearRaw != null && nearRaw !== ''
                ? (typeof nearRaw === 'string' ? JSON.parse(nearRaw) : nearRaw)
                : null;
            mergedAlarm.NearObject = Array.isArray(nearArr) ? nearArr : (nearArr ? [nearArr] : null);
            mergedAlarm.NearName = mergedAlarm.NearObject?.length
                ? mergedAlarm.NearObject.map((r) => r?.name).filter(Boolean).join(', ') || ''
                : '';
        } catch (_) {
            mergedAlarm.NearObject = null;
            mergedAlarm.NearName = '';
        }

        if (existingIndex >= 0) {
            alarms[existingIndex] = mergedAlarm;
        } else {
            alarms.push(mergedAlarm);
        }

        AlarmStorage.saveActiveAlarms(alarms);
    },

    /**
     * Remove an alarm from storage by AlarmId
     * @param {string} alarmId - AlarmId to remove
     */
    removeAlarm: (alarmId) => {
        const alarms = AlarmStorage.getActiveAlarms();
        const filtered = alarms.filter(a => a.AlarmId !== alarmId);
        AlarmStorage.saveActiveAlarms(filtered);
    },

    /**
     * Clear all alarms from storage
     */
    clearAllAlarms: () => {
        try {
            sessionStorage.removeItem(ACTIVE_ALARMS_STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing active alarms from storage:', error);
        }
    },

    /**
     * Get a specific alarm by AlarmId
     * @param {string} alarmId - AlarmId to retrieve
     * @returns {Object|null} Alarm object or null if not found
     */
    getAlarmById: (alarmId) => {
        const alarms = AlarmStorage.getActiveAlarms();
        return alarms.find(a => a.AlarmId === alarmId) || null;
    }
};

// 5.0 cloud alarm storage (native snake_case schema; for CurrentCloudAlerts / DemoPanelCloud)
const CloudAlarmStorage = {
    getActiveAlarms: () => {
        try {
            const stored = sessionStorage.getItem(ACTIVE_CLOUD_ALARMS_STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error reading active cloud alarms from storage:', error);
            return [];
        }
    },

    saveActiveAlarms: (alarms) => {
        try {
            sessionStorage.setItem(ACTIVE_CLOUD_ALARMS_STORAGE_KEY, JSON.stringify(alarms));
        } catch (error) {
            console.error('Error saving active cloud alarms to storage:', error);
        }
    },

    addOrUpdateAlarm: (alarm) => {
        if (!alarm || alarm.event_id == null) {
            console.warn('Invalid cloud alarm data, cannot store:', alarm);
            return;
        }
        const alarms = CloudAlarmStorage.getActiveAlarms();
        const existingIndex = alarms.findIndex(a => a.event_id === alarm.event_id);
        const merged = existingIndex >= 0 ? { ...alarms[existingIndex], ...alarm } : { ...alarm };
        merged.status = deriveCloudAlarmStatus(merged);
        // Resident display reads the nested residents[] array (supports multiple
        // residents per alarm). Normalize it into a consistent {resident_id,
        // resident_name} shape so the 5.0 subscription and the get-alerts-cloud
        // reload look identical regardless of the partner payload shape. Fallback:
        // some payloads carry only the flat top-level resident fields with an empty
        // array — synthesize one entry so those single-resident alarms still render.
        let residents = Array.isArray(merged.residents) ? merged.residents : [];
        if (residents.length === 0 && (merged.resident_id || merged.resident_name)) {
            residents = [{
                resident_id: merged.resident_id,
                resident_name: merged.resident_name,
                resident_first_name: merged.resident_first_name,
                resident_last_name: merged.resident_last_name,
            }];
        }
        merged.residents = residents
            .map((r) => ({
                resident_id: r?.resident_id ?? 0,
                resident_name: r?.resident_name
                    || [r?.resident_first_name, r?.resident_last_name].filter(Boolean).join(' ')
                    || '',
            }))
            .filter((r) => r.resident_id || r.resident_name);
        if (existingIndex >= 0) alarms[existingIndex] = merged;
        else alarms.push(merged);
        CloudAlarmStorage.saveActiveAlarms(alarms);
    },

    removeAlarm: (eventId) => {
        const alarms = CloudAlarmStorage.getActiveAlarms().filter(a => a.event_id !== eventId);
        CloudAlarmStorage.saveActiveAlarms(alarms);
    },

    clearAllAlarms: () => {
        try {
            sessionStorage.removeItem(ACTIVE_CLOUD_ALARMS_STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing active cloud alarms from storage:', error);
        }
    },

    getAlarmByEventId: (eventId) => {
        const alarms = CloudAlarmStorage.getActiveAlarms();
        return alarms.find(a => a.event_id === eventId) || null;
    }
};

const AlertNotificationProvider = ({ children }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [isDataStale, setIsDataStale] = useState(false);
    const [activeAlarms, setActiveAlarms] = useState([]);
    const [activeCloudAlarms, setActiveCloudAlarms] = useState([]);
    const subscriptionRef = useRef(null);
    const lastConfiguredIsCloud = useRef(null);
    const clientRef = useRef(null);
    const initialLoadDone = useRef(false);
    const refreshInProgressRef = useRef(false);

    // Connection resilience refs
    const alertViewerCountRef = useRef(0);              // Count of mounted alert pages (CurrentAlertsV2, DemoPanel, etc.)
    const lastMessageTimestampRef = useRef(Date.now());  // When last subscription message arrived
    const wasOfflineRef = useRef(false);                 // Track offline→online transitions
    const reconcileTimerRef = useRef(null);              // Periodic reconciliation interval (active only when viewers > 0)
    const lastHiddenTimestampRef = useRef(null);          // When the document was last hidden (for sleep/wake detection)

    // Load active alarms from storage on mount (4.0 and 5.0 separate)
    useEffect(() => {
        setActiveAlarms(AlarmStorage.getActiveAlarms());
        setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
    }, []);

    /**
     * Configure Amplify for 4.0 or 5.0 AppSync based on is_cloud. Reconfigures when is_cloud changes.
     * @param {boolean} isCloud - true for 5.0 schema, false for 4.0
     * @returns {boolean} true if configured successfully
     */
    const ensureAmplifyConfigured = useCallback((isCloud) => {
        if (lastConfiguredIsCloud.current === isCloud && clientRef.current) {
            return true;
        }
        try {
            const { apiUrl, apiKey } = getAppSyncConfig(isCloud);
            const region = import.meta.env.VITE_APPSYNC_REGION;

            if (!apiUrl) {
                const msg = isCloud ? 'VITE_*_APPSYNC_50_API_URL not configured' : 'VITE_APPSYNC_API_URL not configured';
                console.warn(msg);
                setConnectionError('AppSync API URL not configured');
                return false;
            }

            const amplifyConfig = {
                API: {
                    GraphQL: {
                        endpoint: apiUrl,
                        region: region,
                        defaultAuthMode: apiKey ? 'apiKey' : 'lambda',
                        ...(apiKey && { apiKey })
                    }
                }
            };

            Amplify.configure(amplifyConfig);
            clientRef.current = generateClient();
            lastConfiguredIsCloud.current = isCloud;
            // console.log('Amplify configured for AppSync:', {schema: isCloud ? '5.0' : '4.0', endpoint: apiUrl, region, authType: apiKey ? 'API_KEY' : 'AWS_LAMBDA'});
            return true;
        } catch (error) {
            console.error('Error configuring Amplify:', error);
            setConnectionError(`Configuration error: ${error.message}`);
            return false;
        }
    }, []);

    // Get session data
    const getSessionData = () => {
        try {
            return getSession();
        } catch (error) {
            console.error('Error getting session data:', error);
        }
        return null;
    };

    // Get authentication token from session
    const getAuthToken = () => {
        const dData = getSessionData();
        return dData?.tKey || null;
    };

    // Migrated users use the V2 push (subscription) pipeline. Legacy
    // (non-migrated) users render LegacyCurrentAlerts.jsx which still
    // operates on the old polling cadence, so the provider should be a
    // complete no-op for them — no AppSync connection, no initial alarm
    // fetch, no reconciliation timer, no reconnect on online/visibility.
    const isMigratedUser = () => !!getSessionData()?.migrated;

    // Load initial active alarms from DynamoDB
    const loadInitialAlarms = useCallback(async (campusGuid) => {
        // Legacy (non-migrated) users use polling — skip the V2 alarm fetch.
        if (!isMigratedUser()) return false;
        try {
            const dData = getSessionData();
            if (!dData) {
                console.warn('No session data available for loading initial alarms');
                setConnectionError('No session data available for loading alarms');
                setIsDataStale(true);
                return false;
            }

            // If campusGuid is not provided, get it from session
            const finalCampusGuid = campusGuid || campusToGuid(dData, dData.acnt);
            const userId = userToGuid(dData, dData.user);

            // console.log('Loading initial active alarms from DynamoDB...', {campusGuid: finalCampusGuid, userId});

            // Load emergency alarms
            let emergAlarms;
            if (dData?.campusData[dData?.acnt.toString()]?.is_cloud) {
                emergAlarms = await ApiRequester({
                    reqEndpoint: "get-alerts-cloud",
                    CampusId: dData.acnt,
                    ToGet: "emergency",
                    UserId: dData.user,
                    Mode: "standard"
                });
            } else {
                emergAlarms = await ApiRequester({
                    reqEndpoint: "get-alerts",
                    CampusId: finalCampusGuid,
                    mode: "standard",
                    hybrid: dData?.hybrid,
                    legacy: dData?.legacy,
                    new: dData?.new,
                    ToGet: "alarm",
                    UserId: userId,
                    Migrated: dData.migrated
                });
            }

            // // Load maintenance alarms
            // const maintAlarms = await ApiRequester({
            //     reqEndpoint: "get-alerts",
            //     CampusId: finalCampusGuid,
            //     mode: "standard",
            //     hybrid: dData?.hybrid,
            //     legacy: dData?.legacy,
            //     new: dData?.new,
            //     ToGet: "maint",
            //     UserId: userId,
            //     Migrated: dData.migrated
            // });

            const allAlarms = [...(emergAlarms || [])];
            const isCloud = !!dData?.campusData[dData?.acnt.toString()]?.is_cloud;

            if (isCloud) {
                AlarmStorage.clearAllAlarms();
                setActiveAlarms([]);
                const toStore = allAlarms.filter(
                    a => a && a.event_id != null && a.event_datetime_utc != null && a.event_datetime_utc !== ''
                );
                CloudAlarmStorage.clearAllAlarms();
                toStore.forEach(alarm => CloudAlarmStorage.addOrUpdateAlarm(alarm));
                setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
                // console.log(`Loaded ${toStore.length} initial cloud (5.0) alarms from DynamoDB`);
                // console.log(toStore)
            } else {
                CloudAlarmStorage.clearAllAlarms();
                setActiveCloudAlarms([]);
                const toStore = allAlarms.filter(
                    alarm => alarm && alarm.AlarmId && alarm.EventDateTime != null && alarm.EventDateTime !== ''
                        && passesHybridFilter(alarm, dData)
                );
                AlarmStorage.clearAllAlarms();
                if (toStore.length > 0) {
                    toStore.forEach(alarm => AlarmStorage.addOrUpdateAlarm(alarm));
                    setActiveAlarms(AlarmStorage.getActiveAlarms());
                    // console.log(`Loaded ${toStore.length} initial (4.0) alarms from DynamoDB`);
                } else {
                    setActiveAlarms([]);
                }
            }
            setConnectionError(null);
            setIsDataStale(false);
            return true;
        } catch (error) {
            console.error('Error loading initial alarms from DynamoDB:', error);
            setConnectionError(`Alarm refresh error: ${error.message}`);
            setIsDataStale(true);
            return false;
        }
    }, []);

    // Subscribe to emergency alerts using Amplify (4.0 or 5.0 based on dData.is_cloud)
    const subscribeToEmergencyAlerts = useCallback((campusGuid) => {
        // Legacy (non-migrated) users use polling — skip subscription entirely.
        if (!isMigratedUser()) return null;
        initialLoadDone.current = false;

        const dData = getSessionData();
        if (!dData?.acnt) {
            console.warn('No session data or campus for subscription');
            return null;
        }

        const isCloud = !!dData?.campusData[dData?.acnt.toString()]?.is_cloud;
        if (!ensureAmplifyConfigured(isCloud)) {
            setIsDataStale(true);
            return null;
        }

        // Unsubscribe from existing subscription if any
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
            subscriptionRef.current = null;
        }

        const { apiKey } = getAppSyncConfig(isCloud);
        const subscriptionQuery = isCloud ? SUBSCRIPTION_QUERY_50 : SUBSCRIPTION_QUERY_40;
        const campusSubId = isCloud ? Number(dData.acnt) : campusGuid;
        const variables = isCloud ? { campus_id: campusSubId } : { CampusId: campusSubId };

        try {
            if (!clientRef.current) {
                console.error('GraphQL client not initialized');
                setConnectionError('GraphQL client not initialized');
                setIsDataStale(true);
                return null;
            }

            const subscription = clientRef.current.graphql({
                query: subscriptionQuery,
                variables,
                authMode: apiKey ? 'apiKey' : 'lambda'
            }).subscribe({
                next: (data) => {
                    const raw = data.value?.data?.onEmergencyAlert || data.data?.onEmergencyAlert || data;
                    console.log('Received AppSync subscription data:', raw);
                    lastMessageTimestampRef.current = Date.now();

                    if (isCloud) {
                        // 5.0: store native schema; remove if no event_datetime_utc
                        if (raw && raw.event_id != null) {
                            const hasEventDateTime = (raw.event_datetime_utc ?? '') !== '';
                            if (!hasEventDateTime) {
                                CloudAlarmStorage.removeAlarm(raw.event_id);
                            } else {
                                CloudAlarmStorage.addOrUpdateAlarm(raw);
                            }
                            setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
                        }
                    } else {
                        // 4.0: store native schema
                        if (raw && raw.AlarmId) {
                            const hasEventDateTime = (raw.EventDateTime ?? '') !== '';
                            const sessionData = getSessionData();
                            if (!hasEventDateTime) {
                                AlarmStorage.removeAlarm(raw.AlarmId);
                            } else if (passesHybridFilter(raw, sessionData)) {
                                AlarmStorage.addOrUpdateAlarm(raw);
                            } else {
                                // Alarm doesn't pass hybrid filter — remove if it was previously stored
                                AlarmStorage.removeAlarm(raw.AlarmId);
                            }
                            setActiveAlarms(AlarmStorage.getActiveAlarms());
                        }
                    }

                    setIsConnected(true);
                    setConnectionError(null);
                    setIsDataStale(false);
                },
                error: (error) => {
                    console.error('Subscription error:', error);
                    subscriptionRef.current = null;
                    setConnectionError(error.message || 'Subscription error');
                    setIsConnected(false);
                    setIsDataStale(true);
                },
                complete: () => {
                    // WebSocket closed gracefully (server-side timeout, etc.)
                    console.warn('[AlertProvider] Subscription completed (WebSocket closed). Will resubscribe.');
                    subscriptionRef.current = null;
                    setIsConnected(false);
                    setIsDataStale(true);
                    // The session check interval will detect subscriptionRef.current === null
                    // and trigger a resubscribe on the next tick
                }
            });

            subscriptionRef.current = subscription;
            setIsConnected(true);
            setConnectionError(null);
            console.log('Successfully subscribed to emergency alerts');

            if (!initialLoadDone.current) {
                initialLoadDone.current = true;
                loadInitialAlarms(isCloud ? undefined : campusGuid).catch(error => {
                    console.error('Error during initial alarm load:', error);
                });
            }

            return subscription;
        } catch (error) {
            console.error('Error creating subscription:', error);
            subscriptionRef.current = null;
            setConnectionError(`Subscription error: ${error.message}`);
            setIsConnected(false);
            setIsDataStale(true);
            return null;
        }
    }, [loadInitialAlarms, ensureAmplifyConfigured]);

    // Unsubscribe from subscription
    const unsubscribe = useCallback(() => {
        if (subscriptionRef.current) {
            try {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
                setIsConnected(false);
                console.log('Unsubscribed from emergency alerts');
            } catch (error) {
                console.error('Error unsubscribing:', error);
            }
        }
    }, []);

    /**
     * Force tear down dead subscription and resubscribe + full alarm reload.
     * Used by online/offline recovery and visibility recovery.
     */
    const forceReconnect = useCallback(() => {
        if (getSession() == null) return;
        // Legacy users have no live subscription to reconnect to.
        if (!isMigratedUser()) return;
        try {
                        const dData = getSession();
            if (dData?.acnt) {
                console.log('[AlertProvider] Force reconnecting subscription...');
                // Tear down existing (possibly dead) subscription
                if (subscriptionRef.current) {
                    try { subscriptionRef.current.unsubscribe(); } catch (_) {}
                    subscriptionRef.current = null;
                }
                setIsConnected(false);
                // Force Amplify client regeneration to get a fresh WebSocket
                lastConfiguredIsCloud.current = null;
                clientRef.current = null;
                // Reset last-message timestamp so staleness check doesn't re-trigger immediately
                lastMessageTimestampRef.current = Date.now();
                // Resubscribe
                const campusGuid = campusToGuid(dData, dData.acnt);
                subscribeToEmergencyAlerts(campusGuid);
            }
        } catch (error) {
            console.error('[AlertProvider] Error during force reconnect:', error);
        }
    }, [subscribeToEmergencyAlerts]);

    /**
     * Register an alert viewer page (call on mount).
     * While viewer count > 0, periodic reconciliation + staleness checks are active.
     */
    const registerAlertViewer = useCallback(() => {
        alertViewerCountRef.current += 1;
        // console.log(`[AlertProvider] Alert viewer registered (count: ${alertViewerCountRef.current})`);
    }, []);

    /**
     * Unregister an alert viewer page (call on unmount).
     */
    const unregisterAlertViewer = useCallback(() => {
        alertViewerCountRef.current = Math.max(0, alertViewerCountRef.current - 1);
        // console.log(`[AlertProvider] Alert viewer unregistered (count: ${alertViewerCountRef.current})`);
    }, []);

    // Auto-subscribe to emergency alerts when we have session data
    useEffect(() => {
        if (getSession() != null) {
            try {
                                const dData = getSession();

                if (dData?.acnt) {
                    const campusGuid = campusToGuid(dData, dData.acnt);
                    subscribeToEmergencyAlerts(campusGuid);
                }
            } catch (error) {
                console.error('Error setting up subscription:', error);
            }
        }

        return () => {
            unsubscribe();
        };
    }, [subscribeToEmergencyAlerts, unsubscribe]);

    // Reconnect when session changes (e.g. campus switch from CampusDropdown)
    useEffect(() => {
        const checkSession = () => {
            const hasSession = getSession() != null;

            if (!hasSession) {
                if (subscriptionRef.current) {
                    unsubscribe();
                }
                setConnectionError(null);
                setIsDataStale(false);
                return;
            }

            if (!subscriptionRef.current || !isConnected) {
                try {
                                        const dData = getSession();

                    if (dData?.acnt) {
                        const campusGuid = campusToGuid(dData, dData.acnt);
                        subscribeToEmergencyAlerts(campusGuid);
                    }
                } catch (error) {
                    console.error('Error reconnecting subscription:', error);
                }
            } else if (isDataStale && !refreshInProgressRef.current) {
                try {
                                        const dData = getSession();

                    if (dData?.acnt) {
                        const campusGuid = campusToGuid(dData, dData.acnt);
                        refreshInProgressRef.current = true;
                        loadInitialAlarms(dData?.campusData[dData?.acnt.toString()]?.is_cloud ? undefined : campusGuid)
                            .finally(() => {
                                refreshInProgressRef.current = false;
                            });
                    }
                } catch (error) {
                    console.error('Error refreshing stale alarms:', error);
                    refreshInProgressRef.current = false;
                }
            }
        };

        const interval = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [subscribeToEmergencyAlerts, unsubscribe, isConnected, isDataStale, loadInitialAlarms]);

    // -------------------------------------------------------------------------
    // FIX 1: Online/Offline + Visibility listeners (unconditional)
    // Detects internet loss/recovery and tab hide/show. Forces a full
    // tear-down + resubscribe + alarm reload on recovery.
    // -------------------------------------------------------------------------
    useEffect(() => {
        const handleOffline = () => {
            console.warn('[AlertProvider] Browser went offline');
            wasOfflineRef.current = true;
            setIsConnected(false);
            setIsDataStale(true);
        };

        const handleOnline = () => {
            if (!wasOfflineRef.current) return;
            console.log('[AlertProvider] Browser back online — scheduling reconnect');
            wasOfflineRef.current = false;
            // Short delay to let the network stack stabilize
            setTimeout(() => forceReconnect(), RECONNECT_DELAY_MS);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                lastHiddenTimestampRef.current = Date.now();
                return;
            }
            if (document.visibilityState !== 'visible') return;

            // Tab became visible again — determine if reconnect is needed
            const hiddenDuration = lastHiddenTimestampRef.current
                ? Date.now() - lastHiddenTimestampRef.current
                : 0;
            lastHiddenTimestampRef.current = null;

            // Reconnect if:
            //  1. We were offline (wasOfflineRef), OR
            //  2. Subscription object is gone, OR
            //  3. Tab was hidden long enough to indicate sleep/wake (>30s)
            if (wasOfflineRef.current || !subscriptionRef.current || hiddenDuration > SLEEP_THRESHOLD_MS) {
                wasOfflineRef.current = false;
                console.log(`[AlertProvider] Tab visible — reconnecting (offline=${wasOfflineRef.current}, noSub=${!subscriptionRef.current}, hidden=${Math.round(hiddenDuration / 1000)}s)`);
                setTimeout(() => forceReconnect(), RECONNECT_DELAY_MS);
            }
        };

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [forceReconnect]);

    // -------------------------------------------------------------------------
    // FIX 3 + 4: Periodic reconciliation + staleness check
    // Only active when alertViewerCountRef.current > 0 (an alert page is mounted).
    // Every RECONCILE_INTERVAL_MS:
    //   - If no subscription message in STALE_THRESHOLD_MS, mark stale + force reconnect
    //   - Otherwise, do a background full-refresh to reconcile any missed messages
    // -------------------------------------------------------------------------
    useEffect(() => {
        const tick = () => {
            if (alertViewerCountRef.current <= 0) return;  // No alert page open — skip
            if (refreshInProgressRef.current) return;       // Already refreshing — skip
            if (getSession() == null) return;    // No session — skip
            if (!isMigratedUser()) return;       // Legacy users use polling — skip


            const now = Date.now();
            const msSinceLastMessage = now - lastMessageTimestampRef.current;

            // If no message in a long time and we think we're connected, silently force reconnect.
            // Don't set isDataStale here — this is a background health-check. The stale banner
            // should only appear for user-visible events (going offline, subscription lost, etc.).
            if (msSinceLastMessage > STALE_THRESHOLD_MS && subscriptionRef.current) {
                console.warn(`[AlertProvider] No subscription message in ${Math.round(msSinceLastMessage / 1000)}s — silently forcing reconnect`);
                forceReconnect();
                return;
            }

            // Otherwise, do a quiet background reconciliation
            try {
                                const dData = getSession();
                if (dData?.acnt) {
                    const isCloud = !!dData.campusData?.[dData.acnt.toString()]?.is_cloud;
                    const campusGuid = isCloud ? undefined : campusToGuid(dData, dData.acnt);
                    refreshInProgressRef.current = true;
                    loadInitialAlarms(campusGuid).finally(() => {
                        refreshInProgressRef.current = false;
                    });
                }
            } catch (error) {
                console.error('[AlertProvider] Error during periodic reconciliation:', error);
                refreshInProgressRef.current = false;
            }
        };

        reconcileTimerRef.current = setInterval(tick, RECONCILE_INTERVAL_MS);
        return () => clearInterval(reconcileTimerRef.current);
    }, [forceReconnect, loadInitialAlarms]);

    // Resubscribe to current campus from session (e.g. CampusDropdown)
    const resubscribeToCurrentCampus = useCallback(() => {
        // Legacy users have no live subscription to resubscribe to.
        if (!isMigratedUser()) return;
        try {
            if (getSession() != null) {
                                const dData = getSession();

                if (dData?.acnt) {
                    const campusGuid = campusToGuid(dData, dData.acnt);
                    console.log('Resubscribing to new campus:', campusGuid);
                    subscribeToEmergencyAlerts(campusGuid);
                }
            }
        } catch (error) {
            console.error('Error resubscribing to current campus:', error);
        }
    }, [subscribeToEmergencyAlerts]);

    const value = {
        isConnected,
        connectionError,
        isDataStale,
        subscribeToEmergencyAlerts,
        unsubscribe,
        resubscribeToCurrentCampus,
        // Alert viewer registration — call registerAlertViewer() on mount and
        // unregisterAlertViewer() on unmount in CurrentAlertsV2, CurrentCloudAlerts,
        // DemoPanel, DemoPanelCloud. Enables periodic reconciliation + staleness checks.
        registerAlertViewer,
        unregisterAlertViewer,
        reconnect: () => {
            unsubscribe();
            if (getSession() != null) {
                try {
                                        const dData = getSession();
                    if (dData?.acnt) {
                        const campusGuid = campusToGuid(dData, dData.acnt);
                        subscribeToEmergencyAlerts(campusGuid);
                    }
                } catch (error) {
                    console.error('Error reconnecting:', error);
                }
            }
        },
        // 4.0 active alarms (CurrentAlertsV2, DemoPanel)
        activeAlarms,
        getActiveAlarms: () => AlarmStorage.getActiveAlarms(),
        getAlarmById: (alarmId) => AlarmStorage.getAlarmById(alarmId),
        removeAlarm: (alarmId) => {
            AlarmStorage.removeAlarm(alarmId);
            setActiveAlarms(AlarmStorage.getActiveAlarms());
        },
        clearAllAlarms: () => {
            AlarmStorage.clearAllAlarms();
            setActiveAlarms([]);
        },
        refreshAlarms: () => {
            setActiveAlarms(AlarmStorage.getActiveAlarms());
        },
        // 5.0 cloud active alarms (CurrentCloudAlerts emergData, DemoPanelCloud)
        activeCloudAlarms,
        getActiveCloudAlarms: () => CloudAlarmStorage.getActiveAlarms(),
        getCloudAlarmByEventId: (eventId) => CloudAlarmStorage.getAlarmByEventId(eventId),
        removeCloudAlarm: (eventId) => {
            CloudAlarmStorage.removeAlarm(eventId);
            setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
        },
        clearAllCloudAlarms: () => {
            CloudAlarmStorage.clearAllAlarms();
            setActiveCloudAlarms([]);
        },
        refreshCloudAlarms: () => {
            setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
        }
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export default AlertNotificationProvider;
